import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { OrderCard } from "@/components/admin/OrderCard";
import { FulfillmentModeToggle } from "@/components/admin/FulfillmentModeToggle";
import {
  ExpeditionConferenceFields,
  type ExpeditionConditionInfo,
  type ExpeditionConferenceValue,
} from "@/components/admin/ExpeditionConferenceFields";
import { fetchAdminCustomer } from "@/lib/adminCustomersApi";
import { fetchExpeditionPricingPreview, type ExpeditionPricingPreview } from "@/lib/ordersApi";
import { OrderDetailDialog } from "@/components/admin/OrderDetailDialog";
import { OrderDatesDialog } from "@/components/admin/OrderDatesDialog";
import { RestoreOrderDialog } from "@/components/admin/RestoreOrderDialog";
import { ScheduleProductionDialog } from "@/components/admin/ScheduleProductionDialog";
import { useApp } from "@/store/useApp";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DEFAULT_ORDER_DATE_TYPES,
  ORDER_DATE_TYPE_LABELS,
  ORDER_DATE_TYPE_ORDER,
  ORDER_STATUS_COLORS,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_ORDER,
  formatBusinessDateShort,
  nextOrderStatus,
  orderMatchesDateFilter,
  todayBusinessDate,
  type OrderDateType,
} from "@/lib/orderUtils";
import {
  fetchAdminOrder,
  fetchAdminOrderSummaries,
  startOrderPurchase,
  toOrderSummaryFromDetail,
  transitionOrder,
  updatePurchaseListItem,
  updateExpeditionListItem,
  updateProductionListItem,
  updateOrderStatus,
} from "@/lib/ordersApi";
import { ApiError } from "@/lib/api";
import { fetchDriverOptions, type DriverOption } from "@/lib/adminDeliveryApi";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AlertTriangle, FileUp, LayoutDashboard, Loader2, Search, Calendar as CalendarIcon, ChevronDown, X } from "lucide-react";
import OrderImportDialog from "@/components/admin/OrderImportDialog";
import { hasPermission, type FulfillmentMode, type OrderDetail, type OrderStatus, type OrderSummary, type ProductionItemStatus } from "@/lib/types";

type TransitionErrorPayload = {
  error?: string;
  message?: string;
  items?: string[];
};

type PendingDialogState = {
  orderId: string;
  toStatus: OrderStatus;
  message: string;
  items: string[];
};

type TransitionModalState = {
  order: OrderSummary;
  toStatus: OrderStatus;
  defaultDate: string;
};

const TRANSITION_DATE_LABELS: Record<OrderStatus, { title: string; description: string; field: string }> = {
  CRIADO: { title: "", description: "", field: "Data" },
  EM_COMPRA: {
    title: "Mover para Em compra",
    description: "Informe a data em que o pedido entrou em compra.",
    field: "Data da compra",
  },
  EM_PRODUCAO: {
    title: "Mover para Em produção",
    description: "Informe a data prevista para iniciar a produção.",
    field: "Data da produção",
  },
  EM_EXPEDICAO: {
    title: "Mover para Expedição",
    description: "Informe a data em que o pedido foi para a expedição.",
    field: "Data da expedição",
  },
  EM_ROTA: {
    title: "Liberar para entrega",
    description: "Mercadoria conferida e liberada para o motorista.",
    field: "Data da entrega",
  },
  ENTREGUE: {
    title: "Marcar como Entregue",
    description: "Informe a data em que o pedido foi entregue.",
    field: "Data da entrega",
  },
  CANCELADO: { title: "", description: "", field: "Data" },
};

type FilterMode = "all" | "day" | "range";

export default function AdminDashboard() {
  const account = useApp((s) => s.account);
  const canEditDates = hasPermission(account, "PEDIDOS.PEDIDO.CRIAR_MANUAL");
  const canRestore = hasPermission(account, "PEDIDOS.PEDIDO.RESTAURAR");
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [clientFilter, setClientFilter] = useState<string>("ALL");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [dateFrom, setDateFrom] = useState<string>(() => todayBusinessDate());
  const [dateTo, setDateTo] = useState<string>(() => todayBusinessDate());
  const [dateTypes, setDateTypes] = useState<OrderDateType[]>(() => [...DEFAULT_ORDER_DATE_TYPES]);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [datesOrder, setDatesOrder] = useState<OrderSummary | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<OrderSummary | null>(null);
  const [scheduleOrder, setScheduleOrder] = useState<OrderSummary | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [pendingDialog, setPendingDialog] = useState<PendingDialogState | null>(null);
  const [transitionModal, setTransitionModal] = useState<TransitionModalState | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [transitionDateInput, setTransitionDateInput] = useState<string>("");
  const [transitionFulfillmentMode, setTransitionFulfillmentMode] = useState<FulfillmentMode>("ENTREGA");
  const [transitionDriverId, setTransitionDriverId] = useState<string>("");
  const [transitionConference, setTransitionConference] =
    useState<ExpeditionConferenceValue>({ nfNumber: "", boletoConfirmed: false, boletoNote: "" });
  const [transitionCondition, setTransitionCondition] = useState<ExpeditionConditionInfo | null>(null);
  const [transitionPricing, setTransitionPricing] = useState<ExpeditionPricingPreview | null>(null);
  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [transitionSubmitting, setTransitionSubmitting] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Ao ativar o filtro por data vindo de "Todos", começa só com Entrega agendada; alternar Dia↔Período
  // preserva o que o operador já escolheu.
  const selectDateMode = (mode: "day" | "range") => {
    if (filterMode === "all") setDateTypes([...DEFAULT_ORDER_DATE_TYPES]);
    setFilterMode(mode);
  };

  const toggleDateType = (type: OrderDateType) => {
    setDateTypes((prev) => {
      if (prev.includes(type)) {
        // Não permite desmarcar a última opção — sempre há ao menos um tipo considerado.
        if (prev.length === 1) return prev;
        return prev.filter((current) => current !== type);
      }
      // Mantém a ordem canônica independentemente da ordem de clique.
      return ORDER_DATE_TYPE_ORDER.filter((candidate) => candidate === type || prev.includes(candidate));
    });
  };

  const loadOrders = useCallback(async () => {
    setLoadState("loading");
    setLoadError(null);
    try {
      const summaries = await fetchAdminOrderSummaries();
      setOrders(summaries);
      setLoadState("ready");
    } catch (error) {
      setOrders([]);
      setLoadState("error");
      setLoadError(error instanceof Error ? error.message : "Nao foi possivel carregar os pedidos");
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const openOrder = async (orderId: string) => {
    setDetailLoading(true);
    setSelectedOrder(null);
    try {
      const detail = await fetchAdminOrder(orderId);
      setSelectedOrder(detail);
    } catch {
      setSelectedOrder(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const replaceOrder = useCallback((updated: OrderDetail) => {
    const summary = toOrderSummaryFromDetail(updated);
    setOrders((current) => {
      const exists = current.some((order) => order.id === updated.id);
      if (exists) return current.map((order) => (order.id === updated.id ? summary : order));
      return [summary, ...current];
    });
    setSelectedOrder((prev) => (prev?.id === updated.id ? updated : prev));
  }, []);

  const runTransition = useCallback(
    async (
      order: OrderSummary,
      toStatus: OrderStatus,
      transitionDate?: string,
      driverId?: string,
      fulfillmentMode?: FulfillmentMode,
      conference?: ExpeditionConferenceValue,
    ) => {
      try {
        const updated = await transitionOrder(order.id, {
          toStatus, transitionDate, driverId, fulfillmentMode,
          nfNumber: conference?.nfNumber.trim() || undefined,
          boletoConfirmed: conference?.boletoConfirmed,
          boletoNote: conference?.boletoNote.trim() || undefined,
          expectedTotal: conference?.expectedTotal,
          expectedSignature: conference?.expectedSignature,
        });
        replaceOrder(updated);
        toast.success(`Pedido movido para ${ORDER_STATUS_LABELS[toStatus]}`);
        return true;
      } catch (error) {
        if (error instanceof ApiError) {
          const payload = error.details as TransitionErrorPayload | null;
          const code = payload?.error;
          if (code === "PENDING_ITEMS") {
            setPendingDialog({
              orderId: order.id,
              toStatus,
              message: payload?.message ?? "Existem itens pendentes",
              items: payload?.items ?? [],
            });
            return false;
          }
          if (code === "MISSING_TRANSITION_DATE" || code === "MISSING_PRODUCTION_DATE" || code === "DRIVER_REQUIRED") {
            const defaultDate = todayBusinessDate();
            setTransitionDateInput(defaultDate);
            setTransitionDriverId("");
            setTransitionFulfillmentMode("ENTREGA");
            setTransitionModal({ order, toStatus, defaultDate });
            return false;
          }
          if (code === "INVALID_TRANSITION" || code === "INVALID_DRIVER" || code === "DRIVER_NOT_FOUND") {
            toast.error(payload?.message ?? "Transição inválida");
            return false;
          }
        }
        toast.error(error instanceof Error ? error.message : "Não foi possível mover o pedido");
        return false;
      }
    },
    [replaceOrder],
  );

  const handleStatusChange = async (
    orderId: string,
    status: OrderStatus,
    transitionDate?: string,
    driverId?: string,
    fulfillmentMode?: FulfillmentMode,
    conference?: ExpeditionConferenceValue,
  ) => {
    const existing = orders.find((o) => o.id === orderId);
    if (existing) {
      const ok = await runTransition(existing, status, transitionDate, driverId, fulfillmentMode, conference);
      if (!ok) throw new Error("transition-blocked");
      return;
    }
    const updated = await updateOrderStatus(orderId, status);
    replaceOrder(updated);
  };

  const handleStartPurchase = async (orderId: string) => {
    const updated = await startOrderPurchase(orderId);
    replaceOrder(updated);
  };

  const handleUpdatePurchaseListItem = async (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      purchased: boolean;
      useStock?: boolean;
      purchasedQuantity?: number;
      reason?: string;
    },
  ) => {
    const updated = await updatePurchaseListItem(orderId, input);
    replaceOrder(updated);
  };

  const handleUpdateProductionListItem = async (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      cut?: string | null;
      status: ProductionItemStatus;
      useStock?: boolean;
      producedQuantity?: number;
      reason?: string;
    },
  ) => {
    const updated = await updateProductionListItem(orderId, input);
    replaceOrder(updated);
  };

  const handleUpdateExpeditionListItem = async (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      cut?: string | null;
      status: "PENDENTE" | "CARREGADO" | "EM_FALTA";
      loadedQuantity?: number;
      reason?: string;
    },
  ) => {
    const updated = await updateExpeditionListItem(orderId, input);
    replaceOrder(updated);
  };

  const handleAdvance = useCallback(
    async (order: OrderSummary) => {
      const next = nextOrderStatus(order.status);
      if (!next) return;
      const defaultDate = todayBusinessDate();
      setTransitionDateInput(defaultDate);
      setTransitionModal({ order, toStatus: next, defaultDate });
    },
    [],
  );

  const handleCancel = useCallback(
    async (order: OrderSummary) => {
      await runTransition(order, "CANCELADO");
    },
    [runTransition],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveDragId(null);
      const orderId = String(event.active.id);
      const targetRaw = event.over?.id;
      if (!targetRaw) return;
      const target = String(targetRaw) as OrderStatus;
      const order = orders.find((o) => o.id === orderId);
      if (!order) return;
      if (target === "CANCELADO") {
        toast.error("Cancelamento deve ser feito pelo menu do pedido");
        return;
      }
      if (order.status === target) return;
      const defaultDate = todayBusinessDate();
      setTransitionDateInput(defaultDate);
      setTransitionDriverId("");
      setTransitionFulfillmentMode("ENTREGA");
      setTransitionConference({ nfNumber: "", boletoConfirmed: false, boletoNote: "" });
      setTransitionCondition(null);
      setTransitionPricing(null);
      if (target === "EM_ROTA" && order.clientId) {
        fetchAdminCustomer(order.clientId)
          .then((c) => setTransitionCondition({
            paymentMethod: c.defaultPaymentMethod ?? null,
            paymentTermDays: c.defaultPaymentTermDays ?? null,
            installments: c.defaultInstallments ?? null,
          }))
          .catch(() => setTransitionCondition(null));
        fetchExpeditionPricingPreview(order.id).then(setTransitionPricing).catch(() => setTransitionPricing(null));
      }
      setTransitionModal({ order, toStatus: target, defaultDate });
    },
    [orders],
  );

  const submitTransitionDate = async () => {
    if (!transitionModal) return;
    if (!transitionDateInput) {
      toast.error("Informe a data");
      return;
    }
    const isRelease = transitionModal.toStatus === "EM_ROTA";
    const pickup = isRelease && transitionFulfillmentMode === "RETIRADA";
    if (isRelease && !pickup && !transitionDriverId) {
      toast.error("Selecione o motorista responsável pela entrega");
      return;
    }
    // NF opcional na conclusão da expedição (obrigatoriedade suspensa por período indeterminado).
    if (isRelease && transitionPricing && !transitionPricing.allPriced) {
      toast.error("Há itens sem preço resolvível. Cadastre o preço na tabela do cliente antes de concluir.");
      return;
    }
    setTransitionSubmitting(true);
    const ok = await runTransition(
      transitionModal.order,
      transitionModal.toStatus,
      transitionDateInput,
      isRelease && !pickup ? transitionDriverId : undefined,
      isRelease ? transitionFulfillmentMode : undefined,
      isRelease ? {
        ...transitionConference,
        expectedTotal: transitionPricing?.totalRaw ?? undefined,
        expectedSignature: transitionPricing?.conferenceSignature ?? undefined,
      } : undefined,
    );
    setTransitionSubmitting(false);
    if (ok) {
      setTransitionModal(null);
      setTransitionDateInput("");
      setTransitionDriverId("");
      setTransitionFulfillmentMode("ENTREGA");
      setTransitionConference({ nfNumber: "", boletoConfirmed: false, boletoNote: "" });
      setTransitionCondition(null);
      setTransitionPricing(null);
    }
  };

  useEffect(() => {
    if (transitionModal?.toStatus === "EM_ROTA" && drivers.length === 0) {
      fetchDriverOptions().then(setDrivers).catch(() => setDrivers([]));
    }
  }, [transitionModal, drivers.length]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return orders.filter((order) => {
      if (clientFilter !== "ALL" && order.clientId !== clientFilter) return false;
      if (!normalizedQuery) return true;
      return (
        order.clientName.toLowerCase().includes(normalizedQuery) ||
        // itemPreview traz o pedido inteiro (backend); buscar no array, não na string de
        // exibição truncada em 2 nomes — senão itens além do 2º não são encontrados.
        order.itemPreview.some((item) => item.toLowerCase().includes(normalizedQuery))
      );
    });
  }, [clientFilter, orders, query]);

  const grouped = useMemo(() => {
    const groups: Record<OrderStatus, OrderSummary[]> = {
      CRIADO: [],
      EM_COMPRA: [],
      EM_PRODUCAO: [],
      EM_EXPEDICAO: [],
      EM_ROTA: [],
      ENTREGUE: [],
      CANCELADO: [],
    };

    if (filterMode === "all") {
      for (const order of filtered) groups[order.status].push(order);
    } else {
      const from = dateFrom;
      const to = filterMode === "day" ? dateFrom : dateTo;
      const [lo, hi] = from <= to ? [from, to] : [to, from];
      for (const order of filtered) {
        // Filtro só decide visibilidade; o pedido permanece na coluna do status atual.
        if (!orderMatchesDateFilter(order, lo, hi, dateTypes)) continue;
        groups[order.status].push(order);
      }
    }

    for (const status of Object.keys(groups) as OrderStatus[]) {
      groups[status].sort(
        (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime(),
      );
    }

    return groups;
  }, [dateFrom, dateTo, dateTypes, filterMode, filtered]);

  const clientOptions = useMemo(
    () =>
      Array.from(new Map(orders.map((order) => [order.clientId, order.clientName])).entries()).map(([id, name]) => ({
        id,
        name,
      })),
    [orders],
  );

  const visibleOrders = useMemo(
    () => (Object.values(grouped) as OrderSummary[][]).flat(),
    [grouped],
  );

  const totalActive = visibleOrders.filter((order) => order.status !== "ENTREGUE" && order.status !== "CANCELADO").length;

  const activeOrder = activeDragId ? orders.find((o) => o.id === activeDragId) ?? null : null;

  const dateTypesLabel = ORDER_DATE_TYPE_ORDER.filter((type) => dateTypes.includes(type))
    .map((type) => ORDER_DATE_TYPE_LABELS[type])
    .join(", ");

  const dateTypesButtonLabel = (() => {
    const first = ORDER_DATE_TYPE_ORDER.find((type) => dateTypes.includes(type));
    const firstLabel = first ? ORDER_DATE_TYPE_LABELS[first] : "";
    const extra = dateTypes.length - 1;
    return extra > 0 ? `${firstLabel} +${extra}` : firstLabel;
  })();

  const filterSummary = (() => {
    if (filterMode === "all") return `Todos os pedidos · ${visibleOrders.length}`;
    if (filterMode === "day") {
      return `Pedidos de ${formatBusinessDateShort(`${dateFrom}T12:00:00-03:00`) ?? dateFrom} · considerando ${dateTypesLabel} · ${visibleOrders.length}`;
    }
    const [lo, hi] = dateFrom <= dateTo ? [dateFrom, dateTo] : [dateTo, dateFrom];
    return `Pedidos de ${formatBusinessDateShort(`${lo}T12:00:00-03:00`) ?? lo} até ${formatBusinessDateShort(`${hi}T12:00:00-03:00`) ?? hi} · considerando ${dateTypesLabel} · ${visibleOrders.length}`;
  })();

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container space-y-5 py-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 px-3 py-1 text-xs font-medium">
                <LayoutDashboard className="h-3 w-3" /> Dashboard de operações
              </div>
              <h1 className="font-display text-3xl font-bold tracking-tight">Pedidos em andamento</h1>
              <p className="mt-1 text-sm text-primary-foreground/80">
                Acompanhe e mova pedidos entre as etapas — do pedido até a entrega.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setImportOpen(true)}
                className="gap-2 bg-primary-foreground text-primary shadow-sm hover:bg-primary-foreground/90"
              >
                <FileUp className="h-4 w-4" /> Importar pedido
              </Button>
              <HeroStat label="Em andamento" value={totalActive} />
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1800px] space-y-4 px-6 py-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente ou item..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="h-11 bg-surface pl-12 text-base"
            />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="h-11 bg-surface lg:w-[220px]">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os clientes</SelectItem>
              {clientOptions.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex h-11 items-center gap-1 rounded-md border bg-surface p-1">
            <FilterTab active={filterMode === "all"} onClick={() => setFilterMode("all")}>
              Todos
            </FilterTab>
            <FilterTab active={filterMode === "day"} onClick={() => selectDateMode("day")}>
              <CalendarIcon className="mr-1 h-3.5 w-3.5" /> Dia
            </FilterTab>
            <FilterTab active={filterMode === "range"} onClick={() => selectDateMode("range")}>
              <CalendarIcon className="mr-1 h-3.5 w-3.5" /> Período
            </FilterTab>
          </div>

          {filterMode !== "all" && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="h-11 justify-between gap-2 bg-surface lg:w-[220px]"
                >
                  <span className="truncate text-sm">
                    <span className="text-muted-foreground">Tipo de data: </span>
                    {dateTypesButtonLabel}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64 p-2">
                <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  Considerar quando o pedido tem…
                </p>
                <div className="space-y-0.5">
                  {ORDER_DATE_TYPE_ORDER.map((type) => {
                    const checked = dateTypes.includes(type);
                    const isLast = checked && dateTypes.length === 1;
                    return (
                      <label
                        key={type}
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted",
                          isLast && "cursor-not-allowed",
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={isLast}
                          onCheckedChange={() => toggleDateType(type)}
                          aria-label={ORDER_DATE_TYPE_LABELS[type]}
                        />
                        <span>{ORDER_DATE_TYPE_LABELS[type]}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="px-2 pb-1 pt-2 text-[11px] leading-snug text-muted-foreground">
                  Vários tipos combinam por “ou”: o pedido aparece se qualquer data marcada estiver no
                  período. Ele permanece na coluna do status atual.
                </p>
              </PopoverContent>
            </Popover>
          )}

          {filterMode === "day" && (
            <div className="flex h-11 items-center gap-2 rounded-md border bg-surface px-3">
              <DatePicker
                value={dateFrom}
                onChange={(next) => setDateFrom(next || todayBusinessDate())}
                className="h-7 w-auto border-0 bg-transparent px-1 font-medium hover:bg-transparent"
              />
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDateFrom(todayBusinessDate())}>
                Hoje
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setFilterMode("all")}
                aria-label="Limpar filtro"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {filterMode === "range" && (
            <div className="flex h-11 items-center gap-2 rounded-md border bg-surface px-3">
              <DatePicker
                value={dateFrom}
                toDate={dateTo || undefined}
                onChange={(next) => {
                  setDateFrom(next);
                  if (next && dateTo && next > dateTo) setDateTo(next);
                }}
                className="h-7 w-auto border-0 bg-transparent px-1 font-medium hover:bg-transparent"
              />
              <span className="text-xs text-muted-foreground">até</span>
              <DatePicker
                value={dateTo}
                fromDate={dateFrom || undefined}
                onChange={(next) => {
                  setDateTo(next);
                  if (next && dateFrom && next < dateFrom) setDateFrom(next);
                }}
                className="h-7 w-auto border-0 bg-transparent px-1 font-medium hover:bg-transparent"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setFilterMode("all")}
                aria-label="Limpar filtro"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground">{filterSummary}</p>

        {loadState === "loading" && (
          <div className="surface-card p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando pedidos...
          </div>
        )}

        {loadState === "error" && (
          <div className="surface-card p-8 text-center text-sm text-destructive">
            {loadError ?? "Nao foi possivel carregar os pedidos"}
          </div>
        )}

        {loadState !== "loading" && loadState !== "error" && (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="overflow-x-auto pb-2">
              <div className="grid min-w-[1740px] grid-cols-6 gap-4">
              {ORDER_STATUS_ORDER.map((status) => (
                <KanbanColumn
                  key={status}
                  status={status}
                  items={grouped[status]}
                  onOpen={(orderId) => void openOrder(orderId)}
                  onAdvance={(order) => void handleAdvance(order)}
                  onCancel={(order) => void handleCancel(order)}
                  onEditDates={canEditDates ? (order) => setDatesOrder(order) : undefined}
                  onRestore={canRestore ? (order) => setRestoreTarget(order) : undefined}
                  onScheduleProduction={(order) => setScheduleOrder(order)}
                  activeDragId={activeDragId}
                />
              ))}
              </div>
            </div>
            <DragOverlay>
              {activeOrder ? (
                <div className="w-full max-w-[260px] rotate-1">
                  <OrderCard order={activeOrder} onOpen={() => {}} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      <Dialog
        open={transitionModal !== null}
        onOpenChange={(open) => {
          if (!open && !transitionSubmitting) {
            setTransitionModal(null);
            setTransitionDateInput("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {transitionModal ? TRANSITION_DATE_LABELS[transitionModal.toStatus].title : ""}
            </DialogTitle>
            <DialogDescription>
              {transitionModal ? TRANSITION_DATE_LABELS[transitionModal.toStatus].description : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {transitionModal ? TRANSITION_DATE_LABELS[transitionModal.toStatus].field : "Data"}
              </label>
              <DatePicker value={transitionDateInput} onChange={setTransitionDateInput} />
            </div>
            {transitionModal?.toStatus === "EM_ROTA" && (
              <>
                <FulfillmentModeToggle
                  value={transitionFulfillmentMode}
                  onChange={setTransitionFulfillmentMode}
                />
                {transitionFulfillmentMode === "ENTREGA" ? (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Motorista responsável *</label>
                    <Select value={transitionDriverId || undefined} onValueChange={setTransitionDriverId}>
                      <SelectTrigger>
                        <SelectValue placeholder={drivers.length === 0 ? "Carregando..." : "Selecione o motorista"} />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700">
                    O cliente vai retirar na fábrica. O pedido fica em <strong>Em entrega</strong> sem motorista e você
                    confirma a entrega manualmente quando ele buscar.
                  </p>
                )}
                <ExpeditionConferenceFields
                  value={transitionConference}
                  onChange={setTransitionConference}
                  condition={transitionCondition}
                  pricing={transitionPricing}
                />
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setTransitionModal(null);
                setTransitionDateInput("");
              }}
              disabled={transitionSubmitting}
            >
              Cancelar
            </Button>
            <Button onClick={() => void submitTransitionDate()} disabled={transitionSubmitting}>
              {transitionSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingDialog !== null}
        onOpenChange={(open) => {
          if (open) return;
          const orderId = pendingDialog?.orderId;
          setPendingDialog(null);
          setTransitionModal(null);
          setTransitionDateInput("");
          if (orderId) void openOrder(orderId);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> Itens pendentes
            </DialogTitle>
            <DialogDescription>{pendingDialog?.message}</DialogDescription>
          </DialogHeader>
          {pendingDialog && pendingDialog.items.length > 0 && (
            <ul className="max-h-64 list-disc space-y-1 overflow-y-auto rounded-md border bg-muted/30 p-4 pl-8 text-sm">
              {pendingDialog.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button
              onClick={() => {
                const orderId = pendingDialog?.orderId;
                setPendingDialog(null);
                setTransitionModal(null);
                setTransitionDateInput("");
                if (orderId) void openOrder(orderId);
              }}
            >
              Corrigir itens
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OrderDetailDialog
        order={selectedOrder}
        loading={detailLoading}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedOrder(null);
            setDetailLoading(false);
          }
        }}
        onChangeStatus={handleStatusChange}
        onStartPurchase={handleStartPurchase}
        onUpdatePurchaseListItem={handleUpdatePurchaseListItem}
        onUpdateProductionListItem={handleUpdateProductionListItem}
        onUpdateExpeditionListItem={handleUpdateExpeditionListItem}
        onOrderUpdated={replaceOrder}
      />
      <OrderDatesDialog
        order={datesOrder}
        onClose={() => setDatesOrder(null)}
        onSaved={replaceOrder}
      />
      <RestoreOrderDialog
        order={restoreTarget}
        onClose={() => setRestoreTarget(null)}
        onRestored={replaceOrder}
      />
      <ScheduleProductionDialog
        order={scheduleOrder}
        onClose={() => setScheduleOrder(null)}
        onSaved={replaceOrder}
      />
      <OrderImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </>
  );
}

function FilterTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded px-3 py-1.5 text-xs font-semibold transition-colors",
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function KanbanColumn({
  status,
  items,
  onOpen,
  onAdvance,
  onCancel,
  onEditDates,
  onRestore,
  onScheduleProduction,
  activeDragId,
}: {
  status: OrderStatus;
  items: OrderSummary[];
  onOpen: (orderId: string) => void;
  onAdvance: (order: OrderSummary) => void;
  onCancel: (order: OrderSummary) => void;
  onEditDates?: (order: OrderSummary) => void;
  onRestore?: (order: OrderSummary) => void;
  onScheduleProduction?: (order: OrderSummary) => void;
  activeDragId: string | null;
}) {
  const colors = ORDER_STATUS_COLORS[status];
  const { setNodeRef, isOver } = useDroppable({ id: status, disabled: status === "CANCELADO" });
  const isSource = Boolean(activeDragId && items.some((i) => i.id === activeDragId));

  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border bg-surface-sunken/30 shadow-sm">
      <div className={cn("flex items-center justify-between border-b px-4 py-3", colors.bg, colors.border)}>
        <div className="flex min-w-0 items-center gap-2">
          <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", colors.dot)} />
          <span className={cn("truncate font-display text-sm font-bold uppercase tracking-wide", colors.text)}>
            {ORDER_STATUS_LABELS[status]}
          </span>
        </div>
        <span
          className={cn(
            "inline-flex h-6 min-w-[26px] items-center justify-center rounded-full px-2 text-xs font-bold tabular-nums",
            "bg-white/70 dark:bg-black/20",
            colors.text,
          )}
        >
          {items.length}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "min-h-[320px] flex-1 space-y-2 p-3 transition-colors",
          isOver && !isSource && "bg-primary/10 ring-2 ring-primary/50 ring-inset",
          status === "CANCELADO" && activeDragId && "opacity-60",
        )}
      >
        {items.length === 0 ? (
          <div className="flex h-full min-h-[120px] items-center justify-center py-8 text-center text-xs text-muted-foreground/70">
            Nenhum pedido
          </div>
        ) : (
          items.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              draggable
              onOpen={onOpen}
              onAdvance={onAdvance}
              onCancel={onCancel}
              onEditDates={onEditDates}
              onRestore={onRestore}
              onScheduleProduction={onScheduleProduction}
              nextStatus={nextOrderStatus(order.status)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function HeroStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-[120px] rounded-xl border border-primary-foreground/20 bg-primary-foreground/10 p-3 backdrop-blur-sm">
      <div className="font-display text-2xl font-bold leading-none tabular-nums">{value}</div>
      <div className="mt-1 text-xs opacity-80">{label}</div>
    </div>
  );
}
